/*
 * Vergrößern per Klick (Bilder, Videos, Vorher/Nachher).
 * Klick auf ein Bild/Video öffnet es groß mit einer Zoom-Animation aus seiner Position.
 * Schließen: Klick irgendwo außerhalb, Taste Esc oder der ×-Knopf.
 * Videos laufen vergrößert mit Ton und Bedienleiste weiter (ab der aktuellen Stelle).
 * Bei „weniger Bewegung“ nur Ein-/Ausblenden, kein Zoom.
 */
(() => {
  if (window.__gsLightbox) return;
  window.__gsLightbox = true;

  const TRIGGERS = [
    '.gs-examples__item .gs-media',
    '.gs-banner__video',
    '.gs-product__stage .gs-scene',
    '.gs-product__stage .gs-media',
    '.gs-product__stage .gs-compare',
    '.gs-pairs__item .gs-compare',
    '.gs-print__item .gs-media',
  ].join(',');
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

  let dialog, stage, closeBtn, current = null;

  const build = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'gs-lightbox';
    dialog.setAttribute('aria-label', document.documentElement.lang === 'de' ? 'Vergrößerte Ansicht' : 'Enlarged view');
    stage = document.createElement('div');
    stage.className = 'gs-lightbox__stage';
    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gs-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Schließen');
    closeBtn.textContent = '×';
    dialog.append(stage, closeBtn);
    document.body.append(dialog);
    // Klick außerhalb des Inhalts schließt
    dialog.addEventListener('click', (e) => {
      if (e.target === closeBtn || !stage.contains(e.target)) close();
    });
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  };

  const largestSrc = (img) => {
    const set = img.getAttribute('srcset');
    if (!set) return img.currentSrc || img.src;
    const last = set.split(',').map((p) => p.trim().split(/\s+/)[0]).pop();
    return last || img.currentSrc || img.src;
  };

  const makeContent = (trigger) => {
    if (trigger.matches('.gs-compare')) {
      const clone = trigger.cloneNode(true);
      clone.classList.remove('gs-zoomable');
      ['data-gs-zoom', 'tabindex', 'role', 'aria-label'].forEach((a) => clone.removeAttribute(a));
      clone.style.aspectRatio = 'auto';
      clone.style.width = '100%';
      clone.style.height = '100%';
      return { el: clone };
    }
    const srcVideo = trigger.querySelector('video');
    if (srcVideo) {
      const v = document.createElement('video');
      const lbSrc = trigger.dataset.gsLightboxSrc;
      v.src = lbSrc || srcVideo.currentSrc || srcVideo.src;
      v.poster = srcVideo.poster || '';
      v.playsInline = true;
      v.controls = true;
      v.autoplay = true;
      v.loop = true;
      v.muted = false;
      if (!lbSrc && srcVideo.currentTime) {
        v.addEventListener('loadedmetadata', () => { v.currentTime = srcVideo.currentTime; }, { once: true });
      }
      return { el: v, video: v, source: srcVideo };
    }
    const img = trigger.querySelector('img');
    if (img) {
      const i = document.createElement('img');
      i.src = largestSrc(img);
      i.alt = img.alt || '';
      return { el: i };
    }
    return null;
  };

  const fit = (ratio) => {
    const maxW = window.innerWidth * 0.92;
    const maxH = window.innerHeight * 0.86;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    return { w, h };
  };

  const open = (trigger) => {
    if (!dialog) build();
    const content = makeContent(trigger);
    if (!content) return;
    const r = trigger.getBoundingClientRect();
    const { w, h } = fit(r.width / r.height);
    stage.style.width = `${w}px`;
    stage.style.height = `${h}px`;
    stage.replaceChildren(content.el);
    content.el.classList.add('gs-lightbox__media');
    current = { trigger, content, wasPlaying: content.source && !content.source.paused };
    if (content.source) content.source.pause();
    document.documentElement.classList.add('gs-lightbox-open');
    dialog.showModal();
    closeBtn.focus({ preventScroll: true });
    if (content.video) content.video.play().catch(() => { content.video.muted = true; content.video.play().catch(() => {}); });

    const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
    const from = `translate(${r.left + r.width / 2 - cx}px, ${r.top + r.height / 2 - cy}px) scale(${r.width / w}, ${r.height / h})`;
    if (reduced()) {
      dialog.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150 });
    } else {
      stage.animate([{ transform: from, borderRadius: '12px' }, { transform: 'none', borderRadius: '16px' }], { duration: 460, easing: EASE });
      dialog.animate([{ backgroundColor: 'rgb(12 6 4 / 0)' }, { backgroundColor: 'rgb(12 6 4 / 0.88)' }], { duration: 360, easing: 'ease-out' });
      closeBtn.animate([{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 220, easing: EASE, fill: 'backwards' });
    }
  };

  const close = () => {
    if (!current || !dialog.open) return;
    const { trigger, content, wasPlaying } = current;
    current = null;
    const finish = () => {
      if (content.video) content.video.pause();
      stage.replaceChildren();
      dialog.close();
      document.documentElement.classList.remove('gs-lightbox-open');
      if (content.source && wasPlaying) content.source.play().catch(() => {});
      trigger.focus({ preventScroll: true });
    };
    if (reduced()) { finish(); return; }
    const r = trigger.getBoundingClientRect();
    const s = stage.getBoundingClientRect();
    const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
    const to = `translate(${r.left + r.width / 2 - cx}px, ${r.top + r.height / 2 - cy}px) scale(${r.width / s.width}, ${r.height / s.height})`;
    closeBtn.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, fill: 'forwards' });
    dialog.animate([{ backgroundColor: 'rgb(12 6 4 / 0.88)' }, { backgroundColor: 'rgb(12 6 4 / 0)' }], { duration: 340, easing: 'ease-in', fill: 'forwards' });
    const a = stage.animate([{ transform: 'none' }, { transform: to, opacity: 0.6 }], { duration: 340, easing: 'cubic-bezier(0.4, 0, 0.6, 1)', fill: 'forwards' });
    a.onfinish = () => {
      finish();
      stage.getAnimations().forEach((x) => x.cancel());
      dialog.getAnimations().forEach((x) => x.cancel());
      closeBtn.getAnimations().forEach((x) => x.cancel());
    };
  };

  const prepare = (root = document) => {
    root.querySelectorAll(TRIGGERS).forEach((el) => {
      if (el.dataset.gsZoom) return;
      if (el.closest('label, a, .gs-lightbox')) return; // Stilkarten und Links nicht überschreiben
      if (!el.matches('.gs-compare') && el.closest('.gs-compare')) return; // Bilder im Vorher/Nachher: ganzer Vergleich öffnet
      el.dataset.gsZoom = '1';
      el.classList.add('gs-zoomable');
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', 'Vergrößern');
    });
  };

  document.addEventListener('click', (e) => {
    const t = e.target.closest('.gs-zoomable');
    if (!t || e.target.closest('button, input, a, .gs-lightbox')) return;
    e.preventDefault();
    open(t);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = e.target.closest?.('.gs-zoomable');
    if (!t || e.target !== t) return;
    e.preventDefault();
    open(t);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare());
  else prepare();
  document.addEventListener('shopify:section:load', (e) => prepare(e.target));
})();
